import React, { ChangeEvent, useContext, useMemo, useState } from 'react'
import Web3 from 'web3'
import { AbiItem } from 'web3-utils'

import Section from '../../components/Section'
import Button from '../../components/Button'
import Input from '../../components/Input'
import { Parameters } from '../../interfaces'
import FormGroup from '../../components/FormGroup'
import './ParametersSection.css'
import MethodInputs from './MethodInputs'
import Select from '../../components/Select'
import UserContext from '../../context/UserContext'
import CopyButton from '../../components/CopyButton'
import toast from 'react-hot-toast'
import { getPriorityFee, getPriorityFromHistory, isArrayType, isBooleanType } from '../../utils'
interface ParametersSectionProps {
    abiFunctions: { [x: string]: any }
    onChange: (parameters: Parameters) => void
    value: Parameters
    networkProvider: string
    scAddress: string
    errors?: string[]
}

const generateUintOptions = () => {
    const options = []
    let i = 0
    while (i <= 256) {
        if (i === 0) {
            options.push({ value: 'uint', label: 'Uint' })
            options.push({ value: 'uint[]', label: 'Uint[]' })
        } else {
            options.push({ value: `uint${i}`, label: `Uint${i}` })
            options.push({ value: `uint${i}[]`, label: `Uint${i}[]` })
        }
        i += 8
    }
    return options
}

const generateBytesOptions = () => {
    const options = [
        // {
        //   value: "byte",
        //   label: "Byte"
        // },{
        //   value: "byte[]",
        //   label: "Byte[]"
        // }
    ]
    let i = 0
    while (i <= 32) {
        if (i === 0) {
            options.push({ value: 'bytes', label: 'Bytes' })
            options.push({ value: 'bytes[]', label: 'Bytes[]' })
        } else {
            options.push({ value: `bytes${i}`, label: `Bytes${i}` })
            options.push({ value: `bytes${i}[]`, label: `Bytes${i}[]` })
        }
        i += 1
    }
    return options
}

const argumentOptions = [
    { value: 'address', label: 'Address' },
    { value: 'address[]', label: 'Address[]' },
    { value: 'string', label: 'String' },
    ...generateUintOptions(),
    ...generateBytesOptions(),
    { value: 'bool', label: 'Bool' },
    { value: 'bool[]', label: 'Bool[]' },
    { value: 'tuple', label: 'Tuple' },
]

const getTypesOptions = (abiFunctions: any) => {
    const types = Object.keys(abiFunctions).filter((item) => item !== 'constructor')

    const typesOptions = [
        {
            value: 'constructor',
            label: 'constructor',
        },
        {
            value: 'function',
            label: 'your function',
        },
    ]

    types.forEach((item) => {
        const foundItem = abiFunctions[item]
        typesOptions.push({
            value: item,
            label:
                foundItem?.stateMutability === 'view' || foundItem?.stateMutability === 'pure'
                    ? `${item} (Read)`
                    : `${item} (Write)`,
        })
    })

    return typesOptions
}

const ParametersSection: React.FC<ParametersSectionProps> = ({
    abiFunctions,
    value,
    networkProvider,
    scAddress,
    onChange,
    errors,
}) => {
    const context = useContext(UserContext)
    const [output, setOutput] = useState<any>('')
    const [nativeValue, setNativeValue] = useState<any>('')
    const onChangeFuncName = (e: ChangeEvent<HTMLInputElement>) => {
        onChange({
            ...value,
            funcName: e.target.value,
        })
    }

    const onChangeType = (e: ChangeEvent<HTMLSelectElement>) => {
        const newType = e.target.value
        const typeDescription = abiFunctions[newType] || {}
        let funcName = ''
        const inputs: any[] = (typeDescription.inputs || []).map((input: any) => {
            return {
                type: input.type,
                name: input.name,
                value: '',
            }
        })
        if (['constructor', 'function'].indexOf(newType) === -1) funcName = typeDescription.name
        onChange({
            ...value,
            type: newType,
            funcName,
            inputs,
            stateMutability: typeDescription?.stateMutability,
        })
    }

    const onAddArgument = () => {
        onChange({
            ...value,
            inputs: [
                ...value.inputs,
                {
                    type: '',
                    value: '',
                },
            ],
        })
    }

    const callFunction = async () => {
        let web3: Web3 | undefined = undefined

        if (!web3) {
            try {
                await (window as any).ethereum.enable()
                web3 = new Web3((window as any).ethereum)
            } catch (error: any) {
                toast.error(`Extension error: ${error.stack}`)
                return
            }
        }
        try {
            if (
                ['payable', 'nonpayable'].includes(value.stateMutability) &&
                !context?.walletAddress
            ) {
                toast.error('Please connect Wallet first')
                return
            }
            let contract = new web3.eth.Contract(
                Object.entries(abiFunctions).map(([key, value]) => value) as AbiItem[],
                scAddress
            )
            const opts: any = { from: context?.walletAddress }

            if (value.stateMutability === 'nonpayable' || value.stateMutability === 'payable') {
                const latest = await web3.eth.getBlock('latest')
                const is1559 = latest.baseFeePerGas !== undefined
                if (is1559) {
                    // --- EIP-1559: set your own tip & max fee (in wei) ---
                    const base = web3.utils.toBN(latest.baseFeePerGas!)
                    let tip = await getPriorityFee(web3)
                    if (!tip) {
                        // fallback to feeHistory; if still null, last-resort 1 gwei cap or your policy
                        tip =
                            (await getPriorityFromHistory(web3)) ||
                            web3.utils.toBN(web3.utils.toWei('0.5', 'gwei'))
                    }
                    console.log('🚀 ~ callFunction ~ tip:', tip)
                    // e.g., maxFee = 2 * base + tip (conservative)
                    const maxFee = base.mul(web3.utils.toBN(2)).add(tip!)
                    console.log('🚀 ~ callFunction ~ maxFee:', maxFee)

                    opts.maxPriorityFeePerGas = tip
                    opts.maxFeePerGas = maxFee

                    // (Optional) force EIP-1559 type:
                    opts.type = '0x2'
                } else {
                    // --- Legacy: set a gasPrice (in wei) ---
                    opts.gasPrice = await web3.eth.getGasPrice()
                    // (Optional) force legacy type:
                    opts.type = '0x0'
                }
            }

            let params: any[] = []
            value.inputs?.forEach((item) => {
                params.push(
                    isArrayType(item.type) || isBooleanType(item.type)
                        ? JSON.parse(item?.value)
                        : item?.value
                )
            })
            console.log('🚀 ~ file: index.tsx:182 ~ params ~ input:', value.inputs)
            if (value.stateMutability === 'nonpayable') {
                await contract.methods[value.funcName](...params)
                    .send(opts)
                    .on('receipt', async (receipt: any) => {
                        if (receipt.status) {
                            setOutput(receipt?.transactionHash)
                        } else {
                            setOutput(receipt?.transactionHash)
                        }
                    })
                    .on('error', function (...args: any[]) {
                        setOutput(args[0])
                    })
            } else if (value.stateMutability === 'payable') {
                const response = await contract.methods[value.funcName](...params).send({
                    ...opts,
                    value: +(nativeValue || '0'),
                })
                setNativeValue('')
                setOutput(JSON.stringify(response))
            } else {
                const response = await contract.methods[value.funcName](...params).call()
                setOutput(JSON.stringify(response))
            }
        } catch (error) {
            if (error instanceof Error) {
                toast.error(error.message)
            } else {
                toast.error('An unknown error occurred')
            }
        }
    }

    const onChangeInputs = (inputs: any[]) => {
        onChange({
            ...value,
            inputs,
        })
    }

    const isConstructor = value.type === 'constructor'
    const typesOptions = useMemo(() => getTypesOptions(abiFunctions), [abiFunctions])

    return (
        <>
            <Section className="section-choose" title="Or enter your parameters manually">
                <div className="row choose">
                    <div className="col-md-3">
                        <FormGroup label="Function type">
                            <Select
                                value={value.type}
                                onChange={onChangeType}
                                options={typesOptions}
                            />
                        </FormGroup>
                    </div>
                    <div className="col-md-7">
                        {!isConstructor && (
                            <Input
                                className="method-input__value"
                                onChange={onChangeFuncName}
                                value={value.funcName}
                                placeholder="Enter function name without arguments"
                                type="text"
                                name="listen"
                            />
                        )}
                    </div>
                </div>
                <MethodInputs
                    value={value.inputs}
                    onChange={onChangeInputs}
                    options={argumentOptions}
                    errors={errors}
                />
                <div className="section-choose__buttons">
                    <Button className="add-param" onClick={onAddArgument}>
                        Add argument
                    </Button>
                    <Button className="add-param" onClick={callFunction}>
                        Call
                    </Button>
                    <Input
                        className="method-input__value"
                        onChange={(e) => {
                            setNativeValue(e.target.value)
                        }}
                        value={nativeValue}
                        placeholder="Enter native value"
                        type="text"
                        name="listen"
                    />
                </div>
            </Section>

            <Section className="encoded-section">
                <textarea
                    readOnly
                    placeholder="Output"
                    rows={20}
                    cols={40}
                    autoComplete="off"
                    value={output}
                />
                <CopyButton hover textToCopy={''} disabled={!value} />
            </Section>
        </>
    )
}

export default ParametersSection
